import type * as spine from "@esotericsoftware/spine-webgl";
import type { SpinePreviewApp } from "../../../../lib/SpinePreviewApp";
import type { StaticStackItem } from "../../../../lib/staticLayerStack";
import {
  captureAllFrames,
  captureStaticStackFramesOnly,
  type FrameResult,
} from "../export-animation/utils/exportFrames";
import { encodeVapMp4 } from "./vapMp4";
import {
  getVapOriginalDurationMs,
  getVapPreviewDurationMs,
  normalizeVapEncodeLimits,
  prepareAppsForVapExport,
  type VapEncodeLimits,
  type VapDurationLimits,
} from "./vapCapturePlan";
import {
  cropCenterFromSquare,
  getVapOriginalDimensions,
  getVapPreviewDimensions,
  type VapOutputDimensions,
} from "../../../../utils/fullscreenType";

export interface ExportSpineVapVideosOptions {
  app: SpinePreviewApp | null | undefined;
  spineCanvas: spine.SpineCanvas | null | undefined;
  staticStackItems?: StaticStackItem[];
  spineAboveApp?: SpinePreviewApp | null;
  spineAboveCanvas?: spine.SpineCanvas | null;
  /** 原图 / 缩略图编码参数（fps、码率、最长时长），默认 30/3000/15 与 30/2000/3 */
  encodeLimits?: Partial<VapEncodeLimits>;
  /**
   * @deprecated 使用 encodeLimits
   * 仅传时长时仍生效，fps/码率走默认
   */
  durationLimits?: Partial<VapDurationLimits>;
  /** 0 = 1:1，1 = 9:16 */
  fullscreenType?: number;
  onProgress?: (percent: number) => void;
}

export type { VapEncodeLimits, VapDurationLimits };

export interface ExportSpineVapVideosResult {
  original: Blob;
  preview: Blob;
}

interface VapCaptureContext {
  app: SpinePreviewApp;
  spineCanvas: spine.SpineCanvas;
  spineAboveApp?: SpinePreviewApp | null;
  spineAboveCanvas?: spine.SpineCanvas | null;
  staticStackItems?: StaticStackItem[];
}

async function captureVapFrames(
  ctx: VapCaptureContext,
  outputSize: number,
  fps: number,
  onFrameProgress: (p: number) => void,
  durationMs: number,
) {
  prepareAppsForVapExport(ctx.app, ctx.spineAboveApp);
  const resolvedDurationMs = durationMs;

  return captureAllFrames({
    app: ctx.app,
    spineCanvas: ctx.spineCanvas,
    outputSize,
    fps,
    durationMs: resolvedDurationMs,
    transparentBackground: true,
    staticStackItems: ctx.staticStackItems,
    spineAboveApp: ctx.spineAboveApp,
    spineAboveCanvas: ctx.spineAboveCanvas,
    warmupCycles: 2,
    onFrameProgress,
  });
}

function cropFramesToDimensions(
  frames: FrameResult[],
  dims: VapOutputDimensions,
): HTMLCanvasElement[] {
  if (
    dims.outputWidth === dims.renderSize &&
    dims.outputHeight === dims.renderSize
  ) {
    return frames.map((f) => f.canvas);
  }
  return frames.map((f) =>
    cropCenterFromSquare(f.canvas, dims.outputWidth, dims.outputHeight),
  );
}

/**
 * 导出原图 + 缩略图两档 VAP mp4。
 * fullscreenType=0：512 + 304；fullscreenType=1：先按 912 正方形渲染再居中裁 512×912 / 304×544。
 */
export async function exportSpineVapVideos(
  options: ExportSpineVapVideosOptions,
): Promise<ExportSpineVapVideosResult> {
  const {
    app,
    spineCanvas,
    staticStackItems,
    spineAboveApp,
    spineAboveCanvas,
    encodeLimits,
    durationLimits,
    fullscreenType,
    onProgress,
  } = options;
  const limits = normalizeVapEncodeLimits({
    ...durationLimits,
    ...encodeLimits,
  });
  const originalDims = getVapOriginalDimensions(fullscreenType);
  const previewDims = getVapPreviewDimensions(fullscreenType);

  const useSpine =
    !!app &&
    !!spineCanvas &&
    (app.instances.length > 0 || (spineAboveApp?.instances.length ?? 0) > 0);
  const useStaticOnly =
    !useSpine && !!staticStackItems?.some((i) => i.kind === "img");

  if (!useSpine && !useStaticOnly) {
    throw new Error("没有可渲染的 Spine 或静态图层");
  }

  onProgress?.(0.02);

  let originalFrames: HTMLCanvasElement[];

  if (useSpine) {
    const { frames: originalFramesRaw } = await captureVapFrames(
      {
        app: app!,
        spineCanvas: spineCanvas!,
        spineAboveApp,
        spineAboveCanvas,
        staticStackItems,
      },
      originalDims.renderSize,
      limits.originalFps,
      (p) => onProgress?.(0.02 + p * 0.38),
      getVapOriginalDurationMs(
        [app!, spineAboveApp],
        limits.originalMaxDurationSec,
        limits.originalFps,
      ),
    );
    originalFrames = cropFramesToDimensions(originalFramesRaw, originalDims);
  } else {
    const durationMs = getVapOriginalDurationMs(
      [],
      limits.originalMaxDurationSec,
      limits.originalFps,
    );
    const { frames: originalFramesRaw } = await captureStaticStackFramesOnly({
      staticStackItems: staticStackItems!,
      outputSize: originalDims.renderSize,
      fps: limits.originalFps,
      durationMs,
      transparentBackground: true,
      onFrameProgress: (p) => onProgress?.(0.02 + p * 0.38),
    });
    originalFrames = cropFramesToDimensions(originalFramesRaw, originalDims);
  }

  onProgress?.(0.42);
  const original = await encodeVapMp4({
    frames: originalFrames,
    srcW: originalDims.outputWidth,
    srcH: originalDims.outputHeight,
    fps: limits.originalFps,
    bitrateKbps: limits.originalBitrateKbps,
  });

  onProgress?.(0.62);

  let previewFrames: HTMLCanvasElement[];

  if (useSpine) {
    const previewDurationMs = getVapPreviewDurationMs(
      [app!, spineAboveApp],
      limits.previewMaxDurationSec,
      limits.previewFps,
    );
    const { frames: previewFramesRaw } = await captureVapFrames(
      {
        app: app!,
        spineCanvas: spineCanvas!,
        spineAboveApp,
        spineAboveCanvas,
        staticStackItems,
      },
      previewDims.renderSize,
      limits.previewFps,
      (p) => onProgress?.(0.62 + p * 0.08),
      previewDurationMs,
    );
    previewFrames = cropFramesToDimensions(previewFramesRaw, previewDims);
  } else {
    const durationMs = getVapPreviewDurationMs(
      [],
      limits.previewMaxDurationSec,
      limits.previewFps,
    );
    const { frames: previewFramesRaw } = await captureStaticStackFramesOnly({
      staticStackItems: staticStackItems!,
      outputSize: previewDims.renderSize,
      fps: limits.previewFps,
      durationMs,
      transparentBackground: true,
      onFrameProgress: (p) => onProgress?.(0.62 + p * 0.08),
    });
    previewFrames = cropFramesToDimensions(previewFramesRaw, previewDims);
  }

  onProgress?.(0.72);
  const preview = await encodeVapMp4({
    frames: previewFrames,
    srcW: previewDims.outputWidth,
    srcH: previewDims.outputHeight,
    fps: limits.previewFps,
    bitrateKbps: limits.previewBitrateKbps,
  });

  onProgress?.(1);
  return { original, preview };
}
